import React, { useState } from 'react';
import { Alert, Button, Drawer, Empty, Popconfirm, Space, Spin, Table, Tag, Typography, message } from 'antd';
import { EditOutlined, RobotOutlined, SendOutlined, CheckOutlined, RocketOutlined, StopOutlined, BranchesOutlined } from '@ant-design/icons';
import AiDraftBadge from '../AiDraftBadge';
import { useApiRequest } from '../../hooks/useApiRequest';
import { useResource } from '../../hooks/useResource';
import LibraryItemFormModal from './LibraryItemFormModal';
import LibraryAiReviseModal from './LibraryAiReviseModal';
import { LibraryApproveModal, LibraryRetireModal } from './LibraryDecisionModals';
import { formatDdMmYyyy } from '../../utils/trainingModel';
import {
    LIBRARY_STATUS_LABELS,
    availableActions,
    canEditItem,
    itemNeedsHumanReview,
    citationCheckSummary,
    parseSourceContext,
    parseTags,
} from '../../utils/libraryTemplateModel';

const { Text, Title, Paragraph } = Typography;

const STATUS_COLOURS = { draft: 'default', in_review: 'gold', approved: 'blue', published: 'green', retired: 'red' };

/**
 * One library template, for the platform_admin curator. Lifecycle buttons follow the API's state
 * machine exactly (draft -> in_review -> approved -> published -> retired; a published or retired
 * item is changed only by forking a new version — Commandment VII). Approval is an explicit,
 * acknowledged human step (Commandment IV); AI-sourced text always shows its chip, measured
 * confidence, the model's reasoning refs and any citation warnings.
 *
 * Template text is rendered as plain text only (never as markup).
 */
const LibraryItemDrawer = ({ open, itemId, standardLabel, onClose, onChanged, onOpenItem }) => {
    const { request } = useApiRequest();
    const detail = useResource(
        async () => (await request('GET', `/admin/library-templates/${itemId}`)).data.item,
        [itemId, request],
        { enabled: open && !!itemId },
    );
    const item = detail.data;

    const versions = useResource(
        async () =>
            (await request('GET', '/admin/library-templates', { query: { standardId: item.standardId, controlRef: item.controlRef } })).data.items || [],
        [item?.standardId, item?.controlRef, item?.status, request],
        { enabled: open && !!item },
    );

    const [busy, setBusy] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [reviseOpen, setReviseOpen] = useState(false);
    const [approveOpen, setApproveOpen] = useState(false);
    // The hash of the text on screen when the reviewer clicked Approve — the approval binds to it.
    const [viewedBodyHash, setViewedBodyHash] = useState(null);
    const [retireOpen, setRetireOpen] = useState(false);

    const refresh = () => {
        detail.reload();
        versions.reload();
        onChanged();
    };

    const post = async (key, path, success) => {
        setBusy(key);
        try {
            const result = await request('POST', `/admin/library-templates/${itemId}/${path}`);
            message.success(success);
            return result;
        } catch (err) {
            message.error(err.message || 'That action failed.');
            return null;
        } finally {
            setBusy(null);
        }
    };

    const actions = availableActions(item);
    const ctx = parseSourceContext(item?.sourceContextJson);
    const needsReview = itemNeedsHumanReview(item);
    const warnings = ctx?.citationWarnings || [];
    const citation = item?.sourceType === 'ai_generated' ? citationCheckSummary(ctx) : null;

    const versionColumns = [
        { title: 'Version', dataIndex: 'version', key: 'version', width: 80, render: (v, r) => (r.id === itemId ? <Text strong>v{v} (this)</Text> : `v${v}`) },
        {
            title: 'Status',
            key: 'status',
            render: (_, r) => (
                <Space>
                    <Tag color={STATUS_COLOURS[r.status]}>{LIBRARY_STATUS_LABELS[r.status] || r.status}</Tag>
                    {r.isCurrent && <Tag color="green">Current</Tag>}
                </Space>
            ),
        },
        { title: 'Created', dataIndex: 'createdAt', key: 'createdAt', width: 110, render: (d) => formatDdMmYyyy(d) },
        { title: '', key: 'open', width: 80, render: (_, r) => (r.id === itemId ? null : <Button size="small" onClick={() => onOpenItem(r.id)}>Open</Button>) },
    ];

    return (
        <Drawer open={open} width={900} onClose={onClose} title={item ? `${item.controlRef} - ${item.title}` : 'Library template'} destroyOnClose>
            {detail.status === 'loading' && !item && <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>}
            {detail.status === 'error' && (
                <Alert type="error" showIcon message="Could not load this template" description={detail.error?.message} action={<Button size="small" onClick={detail.reload}>Retry</Button>} />
            )}
            {item && (
                <div data-testid="library-item-drawer-body">
                    <Space wrap style={{ marginBottom: 8 }}>
                        <Tag color={STATUS_COLOURS[item.status]}>{LIBRARY_STATUS_LABELS[item.status] || item.status}</Tag>
                        <Tag>v{item.version}</Tag>
                        {item.isCurrent && <Tag color="green">Current published version</Tag>}
                        <Tag color="blue">{standardLabel(item.standardId)}</Tag>
                        {item.family && <Tag>{item.family}</Tag>}
                        {parseTags(item.tagsJson).map((t) => <Tag key={t}>{t}</Tag>)}
                    </Space>
                    <div style={{ marginBottom: 8 }}>
                        <AiDraftBadge sourceType={item.sourceType} confidence={item.confidence} reviewed={!needsReview} reviewedAt={item.reviewedAt} draftLabel="AI draft - needs approval" />
                        {item.reviewedAt && (
                            <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>Approved {formatDdMmYyyy(item.reviewedAt)}</Text>
                        )}
                        {item.publishedAt && (
                            <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>Published {formatDdMmYyyy(item.publishedAt)}</Text>
                        )}
                    </div>
                    {item.status === 'retired' && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{ marginBottom: 12 }}
                            message={`Retired ${formatDdMmYyyy(item.retiredAt)} (${item.retiredReasonType})`}
                            description={item.retiredReason}
                        />
                    )}

                    <Space wrap style={{ margin: '8px 0 16px' }}>
                        {actions.includes('edit') && canEditItem(item) && (
                            <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>Edit</Button>
                        )}
                        {(item.status === 'draft' || item.status === 'in_review') && (
                            <Button icon={<RobotOutlined />} onClick={() => setReviseOpen(true)}>Revise with AI</Button>
                        )}
                        {actions.includes('submit-review') && (
                            <Popconfirm
                                title="Submit for review?"
                                description="It can then be approved. Edit or AI-revise it first if needed."
                                okText="Submit"
                                onConfirm={async () => {
                                    if (await post('submit', 'submit-review', 'Submitted for review.')) refresh();
                                }}
                            >
                                <Button type="primary" icon={<SendOutlined />} loading={busy === 'submit'} disabled={!item.bodyText}>Submit for review</Button>
                            </Popconfirm>
                        )}
                        {actions.includes('approve') && (
                            <Button
                                type="primary"
                                icon={<CheckOutlined />}
                                onClick={() => {
                                    setViewedBodyHash(item.bodyHash || null);
                                    setApproveOpen(true);
                                }}
                            >
                                Review and approve
                            </Button>
                        )}
                        {actions.includes('publish') && (
                            <Popconfirm
                                title={`Publish v${item.version} as the current version?`}
                                description="Projects will receive this text from now on; any earlier current version stays in history. This version becomes immutable."
                                okText="Publish"
                                onConfirm={async () => {
                                    if (await post('publish', 'publish', 'Published.')) refresh();
                                }}
                            >
                                <Button type="primary" icon={<RocketOutlined />} loading={busy === 'publish'}>Publish</Button>
                            </Popconfirm>
                        )}
                        {actions.includes('retire') && (
                            <Button danger icon={<StopOutlined />} onClick={() => setRetireOpen(true)}>Retire</Button>
                        )}
                        {actions.includes('new-version') && (
                            <Popconfirm
                                title="Fork a new draft version?"
                                description="Creates an editable draft from this text. This version is left untouched."
                                okText="Create draft"
                                onConfirm={async () => {
                                    const result = await post('fork', 'new-version', 'New draft version created.');
                                    if (result) {
                                        onChanged();
                                        onOpenItem(result.data.item.id);
                                    }
                                }}
                            >
                                <Button icon={<BranchesOutlined />} loading={busy === 'fork'}>New version</Button>
                            </Popconfirm>
                        )}
                    </Space>
                    {item.status === 'draft' && !item.bodyText && (
                        <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Add template text (Edit, or Revise with AI) before submitting for review." />
                    )}

                    {citation && (
                        <Alert
                            data-testid="citation-check"
                            type={citation.kind === 'clean' ? 'info' : 'warning'}
                            showIcon
                            style={{ marginBottom: 12 }}
                            message={
                                citation.kind === 'warnings'
                                    ? `${citation.count} citation warning(s) on the AI draft`
                                    : citation.kind === 'not_applicable'
                                      ? 'Reference check not applicable - nothing was checked'
                                      : citation.kind === 'stale'
                                        ? 'Reference check out of date - text was edited after the AI draft'
                                        : citation.kind === 'clean'
                                          ? 'Reference check ran (references only)'
                                          : 'No reference-check result recorded'
                            }
                            description={
                                citation.kind === 'warnings' ? (
                                    <div>
                                        <div>{citation.text}</div>
                                        <ul style={{ margin: 0, paddingLeft: 18 }}>{warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                                    </div>
                                ) : (
                                    citation.text
                                )
                            }
                        />
                    )}
                    {ctx?.changeSummary && (
                        <Alert type="info" showIcon style={{ marginBottom: 12 }} message="What the last AI revision changed" description={ctx.changeSummary} />
                    )}

                    <Title level={5}>Template text</Title>
                    {item.bodyText ? (
                        <div style={{ background: 'var(--color-surface-sunken)', border: '1px solid var(--color-border)', borderRadius: 10, padding: 14, maxHeight: 420, overflow: 'auto' }}>
                            <Paragraph style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}>{item.bodyText}</Paragraph>
                        </div>
                    ) : (
                        <Empty description="No template text yet." />
                    )}

                    {ctx && (ctx.grounding || ctx.reasoningRefs.length > 0) && (
                        <div style={{ marginTop: 16 }}>
                            <Title level={5}>What the model says it relied on</Title>
                            <Text type="secondary" style={{ display: 'block', fontSize: 12 }}>The model's own claims about its grounding - not verified by the platform.</Text>
                            {ctx.grounding && (
                                <Text type="secondary" style={{ display: 'block' }}>
                                    Policy Hub {ctx.grounding.catalog === 'iso27001_controls' ? 'ISO catalogue' : 'framework catalogue'} · {ctx.grounding.versionLabel}
                                </Text>
                            )}
                            {ctx.reasoningRefs.length > 0 && (
                                <ul style={{ marginTop: 6, paddingLeft: 18 }}>
                                    {ctx.reasoningRefs.map((r, i) => <li key={i}>{r}</li>)}
                                </ul>
                            )}
                            {ctx.instruction && <Text type="secondary">Last instruction: {ctx.instruction}</Text>}
                        </div>
                    )}

                    <Title level={5} style={{ marginTop: 20 }}>Version history</Title>
                    {versions.status === 'error' ? (
                        <Alert type="error" showIcon message="Could not load version history" action={<Button size="small" onClick={versions.reload}>Retry</Button>} />
                    ) : (
                        <Table rowKey={(r) => r.id} size="small" columns={versionColumns} dataSource={(versions.data || []).slice().sort((a, b) => b.version - a.version)} pagination={false} loading={versions.status === 'loading' && !versions.data} />
                    )}
                </div>
            )}

            {item && (
                <>
                    <LibraryItemFormModal
                        open={editOpen}
                        item={item}
                        standardOptions={[]}
                        onClose={() => setEditOpen(false)}
                        onSaved={() => {
                            setEditOpen(false);
                            refresh();
                        }}
                    />
                    <LibraryAiReviseModal
                        open={reviseOpen}
                        item={item}
                        onClose={() => setReviseOpen(false)}
                        onRevised={() => {
                            setReviseOpen(false);
                            refresh();
                        }}
                    />
                    <LibraryApproveModal
                        open={approveOpen}
                        item={item}
                        viewedBodyHash={viewedBodyHash}
                        citationSummary={citation}
                        onClose={() => setApproveOpen(false)}
                        onApproved={() => {
                            setApproveOpen(false);
                            refresh();
                        }}
                        onStale={() => {
                            setApproveOpen(false);
                            refresh();
                        }}
                    />
                    <LibraryRetireModal
                        open={retireOpen}
                        item={item}
                        onClose={() => setRetireOpen(false)}
                        onRetired={() => {
                            setRetireOpen(false);
                            refresh();
                        }}
                    />
                </>
            )}
        </Drawer>
    );
};

export default LibraryItemDrawer;
